import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, LogOut, Search, RefreshCw, Loader2, X, ChevronRight, MapPin, Calendar, Menu, Plus, Pencil, Trash2, Archive, MessageSquare, Folder, Wrench, Inbox, Star, Settings } from "lucide-react";
import { toast } from "sonner";
import { api, STATUS_META, SERVICE_OPTIONS, imgUrl, projectCover } from "../lib/site";
import Logo from "../components/Logo";
import ProjectEditor from "../components/admin/ProjectEditor";
import ServiceEditor from "../components/admin/ServiceEditor";
import TestimonialEditor from "../components/admin/TestimonialEditor";
import HeroSettings from "../components/admin/HeroSettings";
import GeneralSettings from "../components/admin/GeneralSettings";
import PageImagesSettings from "../components/admin/PageImagesSettings";

const STATUSES = ["new","contacted","inspection","quote_sent","won","lost","archived"];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: "#6B7280" };
  return <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap" style={{ background: meta.color + "1A", color: meta.color }}>{meta.label}</span>;
}

const TABS = [
  { key: "leads", label: "Forespørsler", icon: Inbox },
  { key: "projects", label: "Prosjekter", icon: Folder },
  { key: "services", label: "Tjenester", icon: Wrench },
  { key: "testimonials", label: "Omtaler", icon: MessageSquare },
  { key: "settings", label: "Innstillinger", icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!r.data.is_admin) { navigate("/admin/login?error=1", { replace: true }); return; }
        setMe(r.data);
      } catch (e) { navigate("/admin/login", { replace: true }); }
      finally { setChecking(false); }
    })();
  }, [navigate]);

  const logout = async () => { try { await api.post("/auth/logout"); } catch(_){ /* ignore */ } navigate("/", { replace: true }); };

  if (checking) return <div className="min-h-screen grid place-items-center bg-[#F7F5F0]"><Loader2 className="animate-spin text-[#1B3626]" /></div>;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#E2DDD5] sticky top-0 z-20">
        <div className="container-page h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Logo size={36} />
            <div className="min-w-0">
              <div className="font-display font-extrabold text-[13px] sm:text-sm leading-tight">Admin</div>
              <div className="text-[9px] sm:text-[10px] tracking-widest text-[#555E68] truncate">NOUROZI EBYGG</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-xs text-[#555E68] truncate max-w-[220px]" data-testid="admin-user-email">{me?.email}</span>
            <button onClick={logout} className="btn-secondary !py-1.5 !px-2.5 sm:!px-3 text-xs sm:text-sm" data-testid="admin-logout"><LogOut size={14}/><span className="hidden sm:inline">Logg ut</span></button>
          </div>
        </div>
        {/* Tabs */}
        <div className="border-t border-[#EEE8DA] bg-[#FAF8F5]">
          <div className="container-page overflow-x-auto no-scrollbar">
            <div className="flex gap-1 sm:gap-2 min-w-max sm:min-w-0 py-2">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#1B3626] text-white" : "text-[#1C1F22] hover:bg-white"}`} data-testid={`tab-${t.key}`}>
                    <Icon size={14}/> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      <main className="container-page py-5 sm:py-8">
        {tab === "leads" && <LeadsTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "settings" && (<><HeroSettings /><PageImagesSettings /><GeneralSettings /></>)}
      </main>
    </div>
  );
}

/* ---------------- LEADS TAB ---------------- */
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_status: {} });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (service) params.set("service", service);
      if (q) params.set("q", q);
      const [l, s] = await Promise.all([api.get(`/admin/leads?${params.toString()}`), api.get(`/admin/leads/stats`)]);
      setLeads(l.data); setStats(s.data);
    } catch (e) { toast.error("Kunne ikke laste forespørsler"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, service]); // eslint-disable-line

  const updateLead = async (id, changes) => {
    try { const r = await api.patch(`/admin/leads/${id}`, changes); setLeads((prev) => prev.map((x) => x.id === id ? r.data : x)); setSelected(r.data); toast.success("Oppdatert"); }
    catch (e) { toast.error("Kunne ikke oppdatere"); }
  };
  const deleteLead = async (id) => {
    if (!window.confirm("Slette forespørselen permanent?")) return;
    try { await api.delete(`/admin/leads/${id}`); setLeads(prev => prev.filter(x => x.id !== id)); setSelected(null); toast.success("Slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };
  const archiveLead = (id) => updateLead(id, { status: "archived" });

  const chips = [
    { k: "", label: "Alle", count: stats.total },
    { k: "new", label: "Nye", count: stats.by_status?.new || 0 },
    { k: "contacted", label: "Kontaktet", count: stats.by_status?.contacted || 0 },
    { k: "inspection", label: "Befaring", count: stats.by_status?.inspection || 0 },
    { k: "quote_sent", label: "Tilbud sendt", count: stats.by_status?.quote_sent || 0 },
    { k: "won", label: "Vunnet", count: stats.by_status?.won || 0 },
    { k: "archived", label: "Arkivert", count: stats.by_status?.archived || 0 },
  ];

  return (
    <>
      <div className="mb-4 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max sm:flex-wrap sm:min-w-0">
          {chips.map((c) => (
            <button key={c.k || "all"} onClick={() => setStatus(c.k)} className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors whitespace-nowrap ${status === c.k ? "bg-[#1B3626] border-[#1B3626] text-white" : "bg-white border-[#D5D0C7] text-[#1C1F22] hover:border-[#1B3626]"}`} data-testid={`chip-${c.k || "all"}`}>
              {c.label}<span className={`inline-flex items-center justify-center min-w-[22px] h-[18px] rounded-full text-[11px] font-bold px-1.5 ${status === c.k ? "bg-white/20 text-white" : "bg-[#EEF4F0] text-[#1B3626]"}`}>{c.count}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-[#E2DDD5] bg-white p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555E68]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Søk navn, telefon, e-post..." className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[#D5D0C7] bg-white text-sm" data-testid="admin-search" />
          </div>
          <button onClick={load} className="btn-primary !py-2.5 !px-3 text-sm" data-testid="admin-refresh"><RefreshCw size={14}/><span className="hidden sm:inline">Søk</span></button>
          <select value={service} onChange={(e) => setService(e.target.value)} className="px-3 py-2.5 rounded-md border border-[#D5D0C7] bg-white text-sm w-full sm:w-auto" data-testid="admin-filter-service">
            <option value="">Alle tjenester</option>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="grid gap-3 sm:hidden">
        {loading && <div className="p-8 text-center bg-white border rounded-xl"><Loader2 className="animate-spin inline text-[#1B3626]"/></div>}
        {!loading && leads.length === 0 && <div className="p-8 text-center text-[#555E68] bg-white border rounded-xl">Ingen forespørsler.</div>}
        {leads.map((l) => (
          <button key={l.id} onClick={() => setSelected(l)} className="text-left rounded-xl bg-white border border-[#E2DDD5] p-4" data-testid={`lead-card-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-display font-bold text-[15px] truncate">{l.name}</div><div className="text-xs text-[#555E68] mt-0.5 flex items-center gap-1"><Phone size={11}/>{l.phone}</div></div>
              <StatusBadge status={l.status} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
              <div className="flex items-center gap-1.5 text-[#555E68]"><Wrench size={12}/><span className="truncate">{l.service}</span></div>
              <div className="flex items-center gap-1.5 text-[#555E68]"><MapPin size={12}/><span className="truncate">{l.city || l.postal_code || "—"}</span></div>
            </div>
            <div className="mt-3 flex items-center justify-between"><span className="text-[11px] text-[#7f7a68]">{new Date(l.created_at).toLocaleDateString("no-NO", { day:"2-digit", month:"short" })}</span><ChevronRight size={16} className="text-[#1B3626]" /></div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block rounded-xl border border-[#E2DDD5] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-[#555E68] text-left"><tr><th className="p-3">Navn</th><th className="p-3">Tjeneste</th><th className="p-3 hidden md:table-cell">Sted</th><th className="p-3 hidden md:table-cell">Mottatt</th><th className="p-3">Status</th><th className="p-3">Handling</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="p-6 text-center"><Loader2 className="animate-spin inline text-[#1B3626]"/></td></tr>}
              {!loading && leads.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#555E68]">Ingen forespørsler.</td></tr>}
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-[#E7E2D7] hover:bg-[#FAF8F5]" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3"><div className="font-semibold">{l.name}</div><div className="text-xs text-[#555E68]">{l.phone}</div></td>
                  <td className="p-3">{l.service}</td>
                  <td className="p-3 hidden md:table-cell">{l.city || l.postal_code}</td>
                  <td className="p-3 hidden md:table-cell">{new Date(l.created_at).toLocaleDateString("no-NO")}</td>
                  <td className="p-3"><StatusBadge status={l.status} /></td>
                  <td className="p-3"><button onClick={() => setSelected(l)} className="text-[#1B3626] font-semibold text-sm hover:underline" data-testid={`open-lead-${l.id}`}>Åpne</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sm:hidden pt-2 pb-1 grid place-items-center"><div className="h-1.5 w-10 rounded-full bg-[#D5D0C7]" /></div>
            <div className="p-4 sm:p-5 border-b border-[#E2DDD5] flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
              <div className="min-w-0"><h3 className="font-display text-lg sm:text-xl font-bold truncate">{selected.name}</h3><div className="text-xs text-[#555E68] mt-0.5 flex items-center gap-1"><Calendar size={11}/>{new Date(selected.created_at).toLocaleString("no-NO")}</div></div>
              <button onClick={() => setSelected(null)} className="p-2 -m-2" data-testid="close-lead"><X size={20}/></button>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <a href={`tel:${selected.phone}`} className="flex items-center gap-2.5 rounded-lg border border-[#E2DDD5] p-3 hover:border-[#1B3626]" data-testid="lead-call"><span className="w-9 h-9 rounded-md bg-[#1B3626] text-white grid place-items-center shrink-0"><Phone size={14}/></span><div className="min-w-0"><div className="text-[11px] uppercase tracking-wider text-[#555E68]">Telefon</div><div className="font-semibold text-sm truncate">{selected.phone}</div></div></a>
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2.5 rounded-lg border border-[#E2DDD5] p-3 hover:border-[#1B3626]" data-testid="lead-email"><span className="w-9 h-9 rounded-md bg-[#1B3626] text-white grid place-items-center shrink-0"><Mail size={14}/></span><div className="min-w-0"><div className="text-[11px] uppercase tracking-wider text-[#555E68]">E-post</div><div className="font-semibold text-sm truncate">{selected.email}</div></div></a>
              </div>
              <div className="rounded-lg border border-[#E2DDD5] p-3 sm:p-4 grid gap-3 sm:grid-cols-2 text-sm">
                <div><div className="text-[11px] uppercase tracking-wider text-[#555E68]">Tjeneste</div><div className="font-semibold">{selected.service}</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-[#555E68]">Adresse</div><div className="font-semibold">{selected.address || "—"}</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-[#555E68]">Sted</div><div className="font-semibold">{selected.postal_code} {selected.city || "—"}</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-[#555E68]">Ønsket oppstart</div><div className="font-semibold">{selected.preferred_time || "—"}</div></div>
              </div>
              <div><div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">Beskrivelse</div><p className="whitespace-pre-line text-[15px] bg-[#FAF8F5] border border-[#E7E2D7] rounded-lg p-3">{selected.description}</p></div>
              {selected.feedback && (
                <div className="rounded-lg border-2 border-[#1B3626] bg-[#EEF4F0] p-4" data-testid="lead-feedback">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-[#1B3626] font-bold flex items-center gap-1.5"><Star size={12} className="fill-[#E8B547] text-[#E8B547]"/> Kundens tilbakemelding</div>
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <Star key={n} size={14} className={n <= (selected.feedback.rating || 0) ? "text-[#E8B547] fill-[#E8B547]" : "text-[#D5D0C7]"}/>)}</div>
                  </div>
                  {selected.feedback.text && <p className="text-sm text-[#1C1F22] whitespace-pre-line">"{selected.feedback.text}"</p>}
                  <div className="mt-2 text-xs text-[#555E68]">– {selected.feedback.name || selected.name} · Mottatt {new Date(selected.feedback.submitted_at).toLocaleString("no-NO")}{selected.feedback.publish ? " · Kan publiseres" : ""}</div>
                </div>
              )}
              {selected.feedback_token && !selected.feedback && (
                <div className="rounded-lg border border-[#E7D9B7] bg-[#F5ECD9] p-3 text-sm text-[#7a5a2a]" data-testid="lead-feedback-pending">
                  <div className="font-bold mb-1">Tilbakemeldingsforespørsel sendt</div>
                  <div className="text-xs">Venter på svar fra kunden. Dette er nøyaktig samme lenke kunden har fått i e-post:</div>
                  <a
                    href={selected.feedback_url || `${window.location.origin}/tilbakemelding-skjema/${selected.feedback_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 block text-[11px] break-all font-mono text-[#1B3626] underline hover:no-underline"
                    data-testid="lead-feedback-link"
                  >
                    {selected.feedback_url || `${window.location.origin}/tilbakemelding-skjema/${selected.feedback_token}`}
                  </a>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(selected.feedback_url || `${window.location.origin}/tilbakemelding-skjema/${selected.feedback_token}`); toast.success("Lenke kopiert"); }}
                    className="mt-2 btn-secondary !py-1.5 !px-3 text-xs"
                    data-testid="lead-feedback-copy"
                  >Kopier lenke</button>
                </div>
              )}
              {selected.image_ids?.length > 0 && (
                <div><div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-2">Bilder ({selected.image_ids.length})</div>
                  <div className="grid grid-cols-3 gap-2">{selected.image_ids.map(id => <a key={id} target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`}><img alt="" className="w-full h-24 object-cover rounded-md border" src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`}/></a>)}</div>
                </div>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-2">Endre status</div>
                <div className="flex flex-wrap gap-2">{STATUSES.map(s => <button key={s} onClick={() => updateLead(selected.id, { status: s })} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${selected.status === s ? "bg-[#1B3626] text-white border-[#1B3626]" : "bg-white border-[#D5D0C7] text-[#1C1F22] hover:border-[#1B3626]"}`} data-testid={`status-${s}`}>{STATUS_META[s].label}</button>)}</div>
              </div>
              <div><div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">Interne notater</div>
                <textarea rows={3} defaultValue={selected.admin_notes || ""} onBlur={(e) => e.target.value !== (selected.admin_notes || "") && updateLead(selected.id, { admin_notes: e.target.value })} className="w-full rounded-md border border-[#D5D0C7] p-3 text-sm" placeholder="Legg til notater..." data-testid="lead-notes" />
              </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-[#E2DDD5] flex flex-wrap items-center justify-between gap-2 sticky bottom-0 bg-white">
              <button onClick={() => deleteLead(selected.id)} className="text-[#8C2F19] font-semibold text-sm inline-flex items-center gap-1.5 hover:underline" data-testid="lead-delete"><Trash2 size={14}/> Slett permanent</button>
              <button onClick={() => archiveLead(selected.id)} className="btn-secondary !py-2 text-sm" data-testid="lead-archive"><Archive size={14}/> Arkiver</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- PROJECTS TAB ---------------- */
function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { setLoading(true); try { const r = await api.get("/projects?only_published=false"); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg sm:text-xl font-bold">Prosjekter ({items.length})</h2>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary !py-2 text-sm" data-testid="new-project"><Plus size={14}/> Nytt prosjekt</button>
      </div>
      {loading && <div className="p-8 text-center bg-white border rounded-xl"><Loader2 className="animate-spin inline text-[#1B3626]"/></div>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(p => (
          <div key={p.id} className="rounded-xl bg-white border border-[#E2DDD5] overflow-hidden" data-testid={`admin-proj-${p.id}`}>
            <div className="aspect-[16/10] bg-[#F1EDE3] overflow-hidden">
              {projectCover(p) ? <img src={projectCover(p)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#B7AF9C] text-sm">Ingen bilde</div>}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between"><span className="text-xs font-bold text-[#1B3626] uppercase tracking-widest">{p.category}</span>{!p.published && <span className="text-[10px] uppercase tracking-widest text-[#8C2F19] font-bold">Skjult</span>}</div>
              <h3 className="mt-1 font-display font-bold">{p.title}</h3>
              <div className="text-xs text-[#555E68] mt-0.5">{p.location}</div>
              <button onClick={() => { setEditing(p); setOpen(true); }} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3626] hover:underline" data-testid={`edit-proj-${p.id}`}><Pencil size={14}/> Rediger</button>
            </div>
          </div>
        ))}
      </div>
      <ProjectEditor open={open} project={editing} onClose={() => setOpen(false)} onSaved={load} />
    </>
  );
}

/* ---------------- SERVICES TAB ---------------- */
function ServicesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { setLoading(true); try { const r = await api.get("/admin/services"); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg sm:text-xl font-bold">Tjenester ({items.length})</h2>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary !py-2 text-sm" data-testid="new-service"><Plus size={14}/> Ny tjeneste</button>
      </div>
      {loading && <div className="p-8 text-center bg-white border rounded-xl"><Loader2 className="animate-spin inline text-[#1B3626]"/></div>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(s => (
          <div key={s.id} className="rounded-xl bg-white border border-[#E2DDD5] overflow-hidden" data-testid={`admin-svc-${s.id}`}>
            <div className="aspect-[16/10] bg-[#F1EDE3] overflow-hidden">
              {imgUrl(s) ? <img src={imgUrl(s)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#B7AF9C] text-sm">Ingen bilde</div>}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between"><span className="text-xs font-bold text-[#1B3626] uppercase tracking-widest">{s.category}</span>{!s.published && <span className="text-[10px] uppercase tracking-widest text-[#8C2F19] font-bold">Skjult</span>}</div>
              <h3 className="mt-1 font-display font-bold">{s.title}</h3>
              <div className="text-xs text-[#555E68] mt-0.5 line-clamp-2">{s.short}</div>
              <button onClick={() => { setEditing(s); setOpen(true); }} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3626] hover:underline" data-testid={`edit-svc-${s.id}`}><Pencil size={14}/> Rediger</button>
            </div>
          </div>
        ))}
      </div>
      <ServiceEditor open={open} service={editing} onClose={() => setOpen(false)} onSaved={load} />
    </>
  );
}

/* ---------------- TESTIMONIALS TAB ---------------- */
function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { setLoading(true); try { const r = await api.get("/admin/testimonials"); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg sm:text-xl font-bold">Kundeomtaler ({items.length})</h2>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary !py-2 text-sm" data-testid="new-testimonial"><Plus size={14}/> Ny omtale</button>
      </div>
      {loading && <div className="p-8 text-center bg-white border rounded-xl"><Loader2 className="animate-spin inline text-[#1B3626]"/></div>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(t => (
          <div key={t.id} className="rounded-xl bg-white border border-[#E2DDD5] p-4" data-testid={`admin-tst-${t.id}`}>
            <div className="flex items-center gap-1">{[1,2,3,4,5].map(n => <Star key={n} size={14} className={n <= (t.rating || 0) ? "text-[#E8B547] fill-[#E8B547]" : "text-[#D5D0C7]"}/>)}</div>
            <p className="mt-2 text-sm text-[#1C1F22] line-clamp-4">"{t.text}"</p>
            <div className="mt-3 text-xs text-[#555E68] flex items-center justify-between">
              <div><strong className="text-[#1C1F22]">{t.name}</strong> · {t.location}</div>
              {!t.published && <span className="text-[10px] uppercase tracking-widest text-[#8C2F19] font-bold">Skjult</span>}
            </div>
            <button onClick={() => { setEditing(t); setOpen(true); }} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3626] hover:underline" data-testid={`edit-tst-${t.id}`}><Pencil size={14}/> Rediger</button>
          </div>
        ))}
      </div>
      <TestimonialEditor open={open} item={editing} onClose={() => setOpen(false)} onSaved={load} />
    </>
  );
}
